import React, { useState, useRef, useEffect } from "react";

import cornerstone from "cornerstone-core";
import cornerstoneTools from "cornerstone-tools";

import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router";
import { toPng } from "html-to-image";
import {
  drawRectangleStatic,
  addStaticLineWithText,
  panImageEvent,
  ZoomMouseWheel,
  addAnnotations,
  normalizeOptAnnotationsByFrame,
  isCornerstoneElementEnabled,
  safeClearToolState,
  safeUpdateImage,
  syncEtdrsDisplayOnOp,
} from "./utils";
import {
  get_slope_from_line,
  intercept_from_line,
  is_inside_polygon,
} from "../utils/basic_utils";
import { drawBrushPixels } from "../tools/util/drawBrush";

import {
  createContours,
  simplifyContour,
  get_thickness_map,
} from "./processing/thickness";
import {
  Etdrs_Segmentation_Api_Call,
  Etdrs_Volumes_Api_Call,
  buildVolumesSaveParams,
  getSavedEtdrsAnnotation,
  hasEtdrsVolumesData,
  shouldCallInitialComputeVolumes,
  extractPointsCoordinates,
  ezdalinescheck,
  get_annotations,
  opMetadataFetch,
  opPoints,
  save_annotations,
  sendFileUpload,
  veriticalMaxDistance,
} from "../actions/dcm_actions";
import { getFluidLabelsFromVolumesData } from "../utils/fluidManualBrush";
import {
  getEtdrsCenterFromAnnotation,
  getEtdrsFirstEntry,
  getEtdrsVolumesFromAnnotation,
} from "../utils/annotationFormat";
import MultiPointProbeTool from "../tools/annotation/MultiPointProbeTool";
import MultiLineTool from "../tools/annotation/MultiLineTool";

import LisonTool from "../tools/annotation/LisonTool";
import { toolStateMap } from "../tools/toolStateManager";
import EtdrsMoveConfirmModal from "../../../Components/EtdrsMoveConfirmModal";
import { snapshotFluidBaselinesForStack } from "../utils/fluidFrameState";
import { resolveSafeViewportVoi } from "../utils/imageContrast";

const getModule = cornerstoneTools.getModule;
const { getters, setters } = getModule("segmentation");

function get_line_pixels(A, B) {
  var m = slope(A, B);
  var b = intercept(A, m);

  var coordinates = [];
  for (var x = A[0]; x <= B[0]; x++) {
    var y = m * x + b;
    coordinates.push([parseInt(x), parseInt(y)]);
  }

  return coordinates;
}
function get_circle_pixels_m(centerX, centerY, radius, start, end) {
  var points = [];
  for (var degree = start; degree < end; degree++) {
    var radians = (degree * Math.PI) / 180;
    var x = centerX + radius * Math.cos(radians);
    var y = centerY + radius * Math.sin(radians);

    points.push([parseInt(x), parseInt(y)]);
  }

  return points;
}

function slope(a, b) {
  if (a[0] == b[0]) {
    return null;
  }

  return (b[1] - a[1]) / (b[0] - a[0]);
}

function intercept(point, slope) {
  if (slope === null) {
    // vertical line
    return point[0];
  }

  return point[1] - slope * point[0];
}

function roundNumbers(number) {
  // Check if the input is a string and convert it to a number
  if (typeof number === "string") {
    number = parseFloat(number);
  }

  // Check if number is a finite number and has more than 3 digits after the decimal point
  if (Number.isFinite(number) && Math.abs(number) >= 0.001) {
    return parseFloat(number.toFixed(3));
  } else {
    return number;
  }
}

let setPref = false;
let latestAnnotationData = null;
export default function OPViewer(props) {
  const dispatch = useDispatch();
  const annotationData = useSelector((state) => state.dcm).dicom_data;
  const dicom_data = useSelector((state) => state.dcm);
  const etdrsActive = useSelector((state) => state.dcm).etdrsActive;
  const etdrsFallBack = useSelector((state) => state.dcm).etdrsFallBack;
  const etdrsValuesFromSegmentationApi = useSelector(
    (state) => state.dcm,
  ).edtrsValuesFromSegmentationApi;
  const etdrsVolumesFromSegmentationApi = useSelector(
    (state) => state.dcm,
  ).etdrsVolumesFromSegmentationApi;
  let { search } = useLocation();

  const query = new URLSearchParams(search);
  const opData = dicom_data.dicom_data[props.id];

  const dcmRef = useRef(null);
  const [currentNumber, setCurrentNumber] = useState(0);
  const [elmentEnabled, setElementEnabled] = useState(false);
  const [etdrsMoveModalOpen, setEtdrsMoveModalOpen] = useState(false);
  const [pendingEtdrsCenter, setPendingEtdrsCenter] = useState(null);

  const prevStartRef = useSelector((state) => state.dcm.prevStartRef);
  const prevStartRefLatest = useRef(prevStartRef);
  const preMoveEtdrsCenterRef = useRef(null);
  const lastAppliedAnnotationSourceVersionRef = useRef(-1);

  useEffect(() => {
    prevStartRefLatest.current = prevStartRef;
  }, [prevStartRef]);
  useEffect(() => {
    latestAnnotationData = dicom_data;
  }, [dicom_data]);

  useEffect(() => {
    dispatch({
      type: "Op_Cornerstone_Ref_Changed",
      OpCornerstoneRef: dcmRef,
    });
  }, [dispatch]);
  const onSaveClick = async () => {
    await save_annotations(
      latestAnnotationData,
      query.get("stack_id"),
      query.get("user_id"),
      query.get("series_no"),
      query.get("role"),
      query.get("form_version_id") ? "additional_form" : "primary_form",
      query.get("form_version_id") ? query.get("form_version_id") : "",
    );
    if (dicom_data.SubmitLisonsStatus) {
      dispatch({
        type: "Lison_Submit_Status_changed",
        SubmitLisonsStatus: false,
      });
    }
  };
  const getEtdrsVolumesFromSegmentationApi = async (
    center,
    saveParams = null,
  ) => {
    return Etdrs_Volumes_Api_Call(
      dicom_data,
      query.get("stack_id"),
      dispatch,
      { x: center?.x || null, y: center?.y || null },
      saveParams,
    );
  };

  const getEtdrsValuesFromSegmentationApi = async (center) => {
    const etdrsValues_From_Segmentation_Api = await Etdrs_Segmentation_Api_Call(
      dicom_data,
      query.get("stack_id"),
      dispatch,
      { x: center?.x || null, y: center?.y || null },
    );
  };
  // Stable, ref-based debounce so timer persists across renders
  const onSaveClickRef = useRef(onSaveClick);
  useEffect(() => {
    onSaveClickRef.current = onSaveClick;
  }, [onSaveClick]);

  const saveTimeoutRef = useRef(null);
  const handleSaveDebounced = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      onSaveClickRef.current();
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const getToolsState = () => {
    // Get tool state for all enabled tools
    const toolData = cornerstoneTools.getToolState(dcmRef.current, "Etdrs");
    if (!prevStartRef?.x && !prevStartRef?.x) {
      // ;
      if (toolData?.data[0] && !setPref) {
        setPref = true;
        // dispatch({
        //   type: "SET_PREV_START_REF",
        //   payload: { ...toolData.data[0].handles.start },
        // });
      }
    }
    const state = {};
    let toools = [...dicom_data.drawingTools];
    toools.push("Etdrs");

    toools.forEach((tool) => {
      const msms = cornerstoneTools.getToolState(dcmRef.current, tool);

      if (tool == "Length" && msms) {
        const pixel_spacing = opData.OP.metadata.pixel_spacing;
        msms.pixelSpacing = pixel_spacing;
        msms.data = msms.data.map((obj) => ({
          ...obj,
          pixelSpacing: {
            rowPixelSpacing: pixel_spacing.rowPixelSpacing,
            colPixelSpacing: pixel_spacing.columnPixelSpacing,
          },
        }));
      }
      state[tool] = msms;
    });

    dispatch({
      type: "SET_ANNOTATION_DATA",
      payload: state,
      id: props.id,
      key: "OP",
    });
    if (dicom_data.SubmitLisonsStatus) {
      handleSaveDebounced();
    }
  };
  const captureScreenshot = async () => {
    // dispatch({ type: "LoadingOverlay_changed", LoadingOverlay: true });
    if (!dicom_data.takeScreenShot) {
      return;
    }
    if (!dicom_data?.LoadingOverlay) {
      dispatch({ type: "LoadingOverlay_changed", LoadingOverlay: true });
    }
    // Add a 3-second delay before taking the screenshot
    await new Promise((resolve) => setTimeout(resolve, 5000));

    try {
      const dataUrl = await toPng(dicom_data?.OpRef?.current, {
        skipFonts: true,
        cacheBust: true,
      });
      const blob = await (await fetch(dataUrl)).blob();
      const randomNumber = Math.floor(1000 + Math.random() * 9000); // Generates a 4-digit random number

      const file = new File(
        [blob],
        `${dicom_data?.basicMetaData?.study_code}_${dicom_data?.basicMetaData?.subject_code}_${dicom_data?.basicMetaData?.visit_label}_${randomNumber}.png`,
        { type: blob.type },
      );
      let responseFileUplaod = await sendFileUpload(
        query.get("stack_id"),
        file,
      );

      dispatch({
        type: "submitDisabled_changed",
        submitDisable: false,
      });
      dispatch({
        type: "mainScreenShot_url_changed",
        mainScreenShotUrl: responseFileUplaod.urls[0],
      });
    } catch (error) {
    } finally {
      // dispatch({ type: "LoadingOverlay_changed", LoadingOverlay: false });

      // setsubmitting(false);
      dispatch({
        type: "Lison_Input_Submit_Status_changed",
        MlLisonsInputsSubmitStatus: false,
      });
    }
    //
  };
  useEffect(() => {
    const stackId = query.get("stack_id");
    const savedEtdrs = getSavedEtdrsAnnotation(
      props?.annotations,
      dicom_data,
      stackId,
    );
    const annotationSourceChanged =
      props.annotationSourceVersion !==
      lastAppliedAnnotationSourceVersionRef.current;

    if (
      hasEtdrsVolumesData(savedEtdrs) &&
      (dicom_data?.etdrsVolumesFromSegmentationApi === null ||
        annotationSourceChanged)
    ) {
      const savedVolumesData = getEtdrsVolumesFromAnnotation(savedEtdrs);
      const savedFluidLabels = getFluidLabelsFromVolumesData(savedVolumesData);
      if (savedFluidLabels.length) {
        dispatch({
          type: "SET_ACTIVE_FLUID_LABELS",
          activeFluidLabels: savedFluidLabels,
        });
      }
      dispatch({
        type: "SET_ETDRS_VOLUMES_FROM_SEGMENTATION_API",
        etdrsVolumesFromSegmentationApi: savedVolumesData,
      });
      lastAppliedAnnotationSourceVersionRef.current =
        props.annotationSourceVersion;
    }

    if (
      shouldCallInitialComputeVolumes(props?.annotations, dicom_data, stackId, {
        role: query.get("role"),
        adjudicatorUserId: query.get("user_id"),
      })
    ) {
      const center =
        getEtdrsCenterFromAnnotation(savedEtdrs) ||
        dicom_data?.dicom_data?.[stackId]?.OP?.annotations?.Etdrs?.data?.[0]
          ?.handles?.start;
      getEtdrsVolumesFromSegmentationApi(center, null);
    }
    if (
      !getEtdrsFirstEntry(props?.annotations?.Etdrs)?.circleData &&
      dicom_data?.edtrsValuesFromSegmentationApi === null &&
      dicom_data?.aiSegmentationData !== null &&
      !dicom_data.fluidSegmentation
    ) {
      getEtdrsValuesFromSegmentationApi();
    }
    const savedEtdrsEntry = getEtdrsFirstEntry(props?.annotations?.Etdrs);
    if (
      savedEtdrsEntry?.circleData &&
      dicom_data?.edtrsValuesFromSegmentationApi === null
    ) {
      dispatch({
        type: "SET_ETDRS_VALUES_FROM_SEGMENTATION_API",
        edtrsValuesFromSegmentationApi: savedEtdrsEntry.circleData,
      });
    }
    if (!prevStartRef?.x && !prevStartRef?.y) {
      const etdrsCenter = getEtdrsCenterFromAnnotation(props?.annotations?.Etdrs);
      if (etdrsCenter) {
        dispatch({
          type: "SET_PREV_START_REF",
          payload: { ...etdrsCenter },
        });
      }

      // else {
      //   let rect = opData?.OPT?.metadata;

      //   dispatch({
      //     type: "SET_PREV_START_REF",
      //     payload: {
      //       active: false,
      //       drawnIndependently: true,
      //       hasMoved: true,
      //       moving: false,
      //       x: (rect[2] - rect[0]) / 2 + rect[0],
      //       y: (rect[3] - rect[1]) / 2 + rect[1],
      //     },
      //   });
      // }
    }

    if (props?.annotations?.lisonsAreasData) {
      dispatch({
        type: "Set_Lisons_Area_Data",
        lisonsAreasData: props?.annotations?.lisonsAreasData,
      });
      const areaTypes = props?.annotations?.lisonsAreasData?.area_versions?.map(
        (obj) => obj.area_type,
      );

      let options = {
        ...dicom_data.lisonOptions,
      };

      options["lisonAreaOptions"] = areaTypes;
      dispatch({
        type: "Lison_Option_changed",
        lisonOptions: options,
      });
    } else if (
      getEtdrsFirstEntry(savedEtdrs)?.areas &&
      !dicom_data?.lisonsAreasData?.area_versions?.length
    ) {
      const etdrsAreas = getEtdrsFirstEntry(savedEtdrs).areas;
      const areaType =
        dicom_data?.lisonOptions?.lisonAreaselected || "modified_spline";
      dispatch({
        type: "Set_Lisons_Area_Data",
        lisonsAreasData: {
          area_versions: [
            {
              area_type: areaType,
              ...etdrsAreas,
            },
          ],
        },
      });
    }
  }, [
    props.annotations,
    props.annotationSourceVersion,
    dicom_data.fluidSegmentation,
    dicom_data.etdrsVolumesFromSegmentationApi,
    dicom_data.fetchedId,
    dicom_data.opMetaData,
  ]);

  useEffect(() => {
    if (!elmentEnabled || !dcmRef.current) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      try {
        cornerstone.resize(dcmRef.current);
      } catch (_error) {
        // Element may be mid-layout during splitter drag.
      }
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [props.windowWidth, dicom_data.width, elmentEnabled]);

  function compareMlLisonsInput(data1, data2) {
    if (data1.length !== data2.length) {
      return false; // Different number of elements in the arrays
    }

    for (let i = 0; i < data1.length; i++) {
      const scan1 = data1[i];
      const scan2 = data2[i];

      if (scan1.b_scan_id !== scan2.b_scan_id) {
        return false; // Different b_scan_id
      }

      if (scan1.lines.length !== scan2.lines.length) {
        return false; // Different number of lines in the scans
      }

      for (let j = 0; j < scan1.lines.length; j++) {
        const line1 = scan1.lines[j];
        const line2 = scan2.lines[j];

        if (
          line1.line_id !== line2.line_id ||
          line1.start_x_coor !== line2.start_x_coor ||
          line1.end_x_coor !== line2.end_x_coor
        ) {
          return false; // Different line data
        }
      }
    }

    return true;
  }
  function checkInputData() {
    if (dicom_data.dicom_data) {
      // var annotations = get_annotations(
      //   dicom_data.dicom_data,
      //   dicom_data.drawingTools
      // );
      // //
      // var stack_ids = Object.keys(annotations);
      // let lisonLines = extractPointsCoordinates(annotations[stack_ids[0]].OPT);
      // dispatch({
      //   type: "Lison_Input_Submit_Status_changed",
      //   MlLisonsInputsSubmitStatus: compareMlLisonsInput(
      //     lisonLines,
      //     dicom_data?.MlLisonsInputData
      //   ),
      // });
    }
  }
  useEffect(() => {
    let timeoutId;

    const delayedCheckInputData = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        if (!elmentEnabled) {
          return;
        } else {
          if (
            dicom_data?.MlLisonsInputData.length !== 0 &&
            dicom_data.LisonActive
          ) {
            checkInputData();
          }
        }
      }, 300);
    };

    delayedCheckInputData();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [dicom_data.dicom_data]);

  const onMeasurementAdded = (e) => {
    // Get tool state for all enabled tools on measurementAdded
    getToolsState();
  };

  const onMeasurementModified = (e) => {
    // Measurement modified
    getToolsState();
  };
  const onWindowResize = () => {
    if (!elmentEnabled || !dcmRef.current) return;
    try {
      cornerstone.updateImage(dcmRef.current);
    } catch (_e) {
      // Element may not be enabled during layout changes (e.g. metadata dialog).
    }
  };

  function findClosest(points, target) {
    let closestDiff = Infinity;
    let closestList = null;

    for (let i = 0; i < points.length; i++) {
      let diff = Math.abs(points[i][1] - target);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestList = i;
      }
    }

    return closestList;
  }

  const onMouseClick = (e) => {
    if (etdrsActive) {
      onMouseUp();
      getToolsState();
    } else {
      setCurrentNumber(currentNumber + 1);
      if (
        dicom_data.selectedTool === "MOUSE" ||
        dicom_data.selectedTool === "ThicknessMap"
      ) {
        const resp = findClosest(
          opData.OPT.metadata.frames,
          e.detail.currentPoints.image.y,
        );

        if (resp) {
          dispatch({
            type: "DICOM_CHANGE_FRAME",
            changeId: props.id,
            changeType: "OP",
            frame: resp,
          });
        }
      }
    }
  };

  const addRectangleStatic = () => {
    const RectangleStatic_v = cornerstoneTools.RectangleStatic;
    cornerstoneTools.addTool(RectangleStatic_v);
    cornerstoneTools.setToolActive("RectangleStatic", { mouseButtonMask: 1 });
  };

  const addLineStatic = () => {
    const LineStatic_v = cornerstoneTools.LineStatic;
    cornerstoneTools.addTool(LineStatic_v);
    cornerstoneTools.setToolActive("LineStatic", { mouseButtonMask: 1 });
  };

  const addThicknessMapStatic = () => {
    const ThicknessMap_v = cornerstoneTools.ThicknessMap;
    cornerstoneTools.addTool(ThicknessMap_v);
    cornerstoneTools.setToolActive("ThicknessMap", { mouseButtonMask: 1 });

    const Etdrs = cornerstoneTools.Etdrs;
    cornerstoneTools.addTool(Etdrs);
    cornerstoneTools.setToolActive("Etdrs", { mouseButtonMask: 1 });
  };

  // On Frame Change
  useEffect(() => {
    if (elmentEnabled) {
      // if(!dicom_data.syncScans){
      //   if(dicom_data.changeId !== props.id){
      //     return
      //   }
      // }
      if (dicom_data.changeId !== props.id) {
        return;
      }

      cornerstoneTools.clearToolState(dcmRef.current, "LineStatic");
      const verticalDistances = [];
      let pointsArray = opData.OPT.metadata.frames;
      for (let i = 1; i < pointsArray.length; i++) {
        const startYDistance = pointsArray[i][1] - pointsArray[i - 1][1];
        const endYDistance = pointsArray[i][3] - pointsArray[i - 1][3];
        verticalDistances.push([startYDistance, endYDistance]);
      }

      addStaticLineWithText(
        dcmRef,
        opData.OPT.metadata.frames[dicom_data.currentFrame],
        `Frame ${dicom_data.currentFrame + 1}`,
      );

      if (opData.OP.vertical_line !== null) {
        addStaticLineWithText(dcmRef, opData.OP.vertical_line, ``);
      }

      cornerstone.updateImage(dcmRef.current);
    }
  }, [dicom_data.currentFrame, opData.OP.vertical_line]);

  const onMouseDrag = (e) => {
    if (dicom_data.selectedKey === "Shift") {
      panImageEvent(e);
      return;
    }

    if (dicom_data.selectedTool !== "DragRL") {
      return;
    }
    // Check if mouse is inside rectangle otherwise ignore
    if (
      !is_inside_polygon(
        [e.detail.currentPoints.image.x, e.detail.currentPoints.image.y],
        opData.OPT.metadata.rect_poly,
      )
    ) {
      return;
    }

    // Drag Left to right or right to left
    let xmin = opData.OPT.metadata.rect[0];
    let xmax = opData.OPT.metadata.rect[2];

    let currentFrameData = opData.OPT.metadata.frames[dicom_data.currentFrame];

    let total_width = xmax - xmin;
    let normalizedX = (e.detail.currentPoints.image.x - xmin) / total_width;

    let slope = get_slope_from_line(currentFrameData);
    let intercept = intercept_from_line(currentFrameData);

    let yh = slope * e.detail.currentPoints.image.x + intercept;

    dispatch({
      type: "DICOM_CHANGE_VERTICAL_LINE",
      OP: [
        e.detail.currentPoints.image.x,
        yh - 50,
        e.detail.currentPoints.image.x,
        yh + 50,
      ],
      OPT: [
        normalizedX * opData.OPT.metadata.meta.width,
        0,
        normalizedX * opData.OPT.metadata.meta.width,
        opData.OPT.metadata.meta.height,
      ],
      id: props.id,
    });
  };

  const onKeyboardKeyDown = (e) => {
    if (e.key === "ArrowUp" && dicom_data.currentFrame === 0) {
      dispatch({
        type: "DICOM_CHANGE_FRAME",
        changeId: props.id,
        changeType: "OP",
        frame: dicom_data.currentFrame + 1,
      });
    }
  };
  useEffect(() => {
    if (!elmentEnabled) return;

    const etdrsToolState = cornerstoneTools.getToolState(
      dcmRef.current,
      "Etdrs",
    );
    const hasToolData = !!(etdrsToolState?.data && etdrsToolState.data.length);
    let shouldUpdateImage = false;

    // Handle fallback: reset start handle to previous or center
    if (etdrsFallBack) {
      dispatch({
        type: "etdrsFallBack_changed",
        etdrsFallBack: false,
      });
      if (hasToolData) {
        const rect = opData.OPT.metadata.rect;
        const defaultX = (rect[2] - rect[0]) / 2 + rect[0];
        const defaultY = (rect[3] - rect[1]) / 2 + rect[1];

        etdrsToolState.data.forEach((data) => {
          data.handles.start = {
            ...data.handles.start,
            x: prevStartRef?.x ?? defaultX,
            y: prevStartRef?.y ?? defaultY,
          };
        });
        shouldUpdateImage = true;
      }
    }
    if (etdrsVolumesFromSegmentationApi && etdrsToolState?.data) {
      etdrsToolState.data.forEach((data) => {
        data.volumesData = etdrsVolumesFromSegmentationApi;
      });
      shouldUpdateImage = true;
    }

    // Inject circle data from compute-etdrs API (independent of ETDRS toggle)
    if (etdrsValuesFromSegmentationApi && hasToolData) {
      etdrsToolState.data.forEach((data) => {
        data.circleData = etdrsValuesFromSegmentationApi;
      });
      shouldUpdateImage = true;
    }

    // Sync visibility and display flags
    if (hasToolData) {
      etdrsToolState.data.forEach((data) => {
        data.currentFrame = dicom_data?.currentFrame;
        if (
          data.circles_radii?.length ||
          data.volumesData ||
          data.handles?.start
        ) {
          data.visible = etdrsActive || data.visible !== false;
        } else {
          data.visible = etdrsActive;
        }
        if (dicom_data?.LisonActive) {
          if (data?.areas?.ETDRS_area_mm2 !== undefined) {
            data.etdrsvaluesShow = true;
          }
        } else if (data.etdrsvaluesShow !== undefined) {
          // Preserve saved visibility on initial load; toggle effect handles user off/on.
        } else {
          data.etdrsvaluesShow = false;
        }
        data.showEtdrsMirroring =
          dicom_data?.settings?.brush?.showEtdrsMirroring;
      });
      shouldUpdateImage = true;
    }

    if (shouldUpdateImage) {
      cornerstone.updateImage(dcmRef.current);
    }

    getToolsState();
  }, [
    etdrsActive,
    dicom_data?.LisonActive,
    dicom_data?.settings?.brush?.showEtdrsMirroring,
    etdrsValuesFromSegmentationApi,
    etdrsVolumesFromSegmentationApi,
    etdrsFallBack,
    dicom_data?.fluidDirtyFrames,
    dicom_data?.currentFrame,
    dicom_data?.activeFluidLabels,
    elmentEnabled,
    props.annotations,
    props.annotationSourceVersion,
  ]);

  useEffect(() => {
    document.addEventListener("keydown", onKeyboardKeyDown);
    // document.addEventListener("keyup", onKeyboardKeyUp);
    // return () => {
    //   document.removeEventListener("keydown", onKeyboardKeyDown);
    //   // document.removeEventListener("keyup", onKeyboardKeyUp);
    // };
  }, []);
  const onMouseWheel = (e) => {
    if (dicom_data.selectedKey === "Shift") {
      // If control is pressed with mouse wheel scroll, we perform zoom in and out
      ZoomMouseWheel(e, cornerstone);
      return;
    }
    // If mouse is on image, we perform custom stack scroll
    if (e.detail.spinY < 0) {
      if (dicom_data.currentFrame > 0) {
        setCurrentNumber(currentNumber - 1);
        dispatch({
          type: "DICOM_CHANGE_FRAME",
          changeId: props.id,
          changeType: "OP",
          frame: dicom_data.currentFrame - 1,
        });
      }
    } else {
      if (dicom_data.currentFrame < opData.OPT.metadata.frames.length - 1) {
        setCurrentNumber(currentNumber + 1);
        dispatch({
          type: "DICOM_CHANGE_FRAME",
          changeId: props.id,
          changeType: "OP",
          frame: dicom_data.currentFrame + 1,
        });
      }
    }
  };
  const pushPoints = (currentRef, pointx, pointy) => {
    // pointx = 35;
    // pointy = 135;
    cornerstoneTools.addToolState(currentRef, "LisonProbe", {
      active: false,
      color: "yellow",
      cachedStats: {
        mo: 125,
        sp: 125,
        storedPixels: [125],
        x: pointx,
        y: pointy,
      },
      handles: {
        end: {
          active: false,
          highlight: true,
          moving: false,
          x: pointx,
          y: pointy,
        },
      },
      invalidated: false,
      uuid: "1d17782a-f5c0-40b8-afa4-51830f01bf6f",
      visible: true,
    });
  };

  const hasSavedEzdaAnnotations = () =>
    getEtdrsFirstEntry(props?.annotations?.Etdrs)?.areas?.ETDRS_area_mm2 !==
      undefined ||
    Boolean(dicom_data?.lisonsAreasData?.area_versions?.length);

  const getOptAnnotationsForProbeDraw = () => {
    const annotations = get_annotations(
      dicom_data.dicom_data,
      dicom_data.drawingTools,
    );
    const stack_ids = Object.keys(annotations);
    if (stack_ids.length) {
      const cornerstoneOpt = annotations[stack_ids[0]]?.OPT;
      if (ezdalinescheck(cornerstoneOpt)) {
        return cornerstoneOpt;
      }
    }

    const savedOpt = normalizeOptAnnotationsByFrame(props.optAnnotations || {});
    if (ezdalinescheck(savedOpt)) {
      return savedOpt;
    }

    return null;
  };

  const drawLisonProbePointsFromOpt = () => {
    if (!dcmRef.current) {
      return false;
    }

    const optAnnotations = getOptAnnotationsForProbeDraw();
    if (!optAnnotations) {
      return false;
    }

    const lisonProbeState = cornerstoneTools.getToolState(
      dcmRef.current,
      "LisonProbe",
    );
    if (lisonProbeState?.data) {
      lisonProbeState.data = [];
    }

    cornerstoneTools.addTool(cornerstoneTools.LisonProbeTool);

    let pointsAdded = 0;
    const lisonLines = extractPointsCoordinates(optAnnotations);
    for (let i = 0; i < lisonLines.length; i++) {
      for (let j = 0; j < lisonLines[i].lines.length; j++) {
        const frame_coords = opData?.OPT?.metadata?.frames[lisonLines[i].b_scan_id];
        const Framecoords = opData?.OPT?.metadata?.frames[lisonLines[i]?.b_scan_id];
        if (!Framecoords || !frame_coords) {
          continue;
        }

        const optWidth = opData?.OPT?.metadata?.meta?.width;
        const dx = Framecoords[2] - Framecoords[0];
        const dy = Framecoords[3] - Framecoords[1];
        const redLineLength = Math.sqrt(dx * dx + dy * dy);
        if (!redLineLength || !optWidth) {
          continue;
        }

        const scalingFactor = redLineLength / optWidth;
        const unitVector = [
          (frame_coords[2] - frame_coords[0]) / redLineLength,
          (frame_coords[3] - frame_coords[1]) / redLineLength,
        ];

        const newStartPoint = [
          frame_coords[0] +
            lisonLines[i].lines[j].start_x_coor *
              scalingFactor *
              unitVector[0],
          frame_coords[1] +
            lisonLines[i].lines[j].start_x_coor *
              scalingFactor *
              unitVector[1],
        ];

        pushPoints(
          dcmRef.current,
          parseInt(newStartPoint[0], 10),
          parseInt(newStartPoint[1], 10),
        );
        pointsAdded += 1;

        const newEndPoint = [
          frame_coords[0] +
            lisonLines[i].lines[j].end_x_coor *
              scalingFactor *
              unitVector[0],
          frame_coords[1] +
            lisonLines[i].lines[j].end_x_coor *
              scalingFactor *
              unitVector[1],
        ];

        pushPoints(
          dcmRef.current,
          parseInt(newEndPoint[0], 10),
          parseInt(newEndPoint[1], 10),
        );
        pointsAdded += 1;
      }
    }

    if (pointsAdded === 0) {
      return false;
    }

    cornerstoneTools.setToolPassive("LisonProbe");
    return true;
  };

  const tryDrawLisonProbePoints = (retriesLeft = 12) => {
    if (drawLisonProbePointsFromOpt()) {
      cornerstone.updateImage(dcmRef.current);
      return;
    }

    if (retriesLeft > 0) {
      window.setTimeout(() => tryDrawLisonProbePoints(retriesLeft - 1), 200);
    }
  };

  const addAreas = (pixelatedObject) => {
    const edtrsToolState = cornerstoneTools.getToolState(
      dcmRef.current,
      "Etdrs",
    );
    if (edtrsToolState?.data) {
      edtrsToolState.data.forEach((data) => {
        data.visible = etdrsActive; // Toggle the visibility
      });

      cornerstone.updateImage(dcmRef.current); // Force re-render
    }

    if (edtrsToolState && edtrsToolState.data.length > 0) {
      // Assuming we're modifying the first instance of the tool

      const toolInstance = edtrsToolState.data[0];

      // Step 2: Update the properties of the tool instance
      toolInstance.areas = {
        lesions_1mm_area_mm2: pixelatedObject?.lesions_1mm_area_mm2,
        lesions_1mm_percentage: pixelatedObject?.lesions_1mm_percentage,
        lesions_3mm_area_mm2: pixelatedObject?.lesions_3mm_area_mm2,
        lesions_3mm_percentage: pixelatedObject?.lesions_3mm_percentage,
        lesions_6mm_area_mm2: pixelatedObject?.lesions_6mm_area_mm2,
        lesions_6mm_percentage: pixelatedObject?.lesions_6mm_percentage,
        ETDRS_area_mm2: pixelatedObject?.ETDRS_area_mm2,
        ETDRS_percentage: pixelatedObject?.ETDRS_percentage,
      };
      cornerstone.updateImage(dcmRef.current);
    } else {
      console.error("Tool state not found for 'Etdrs'.");
    }
  };
  const AddLisonAreas = async (dcmRef, objectType, displayType) => {
    let totalArea, midx, midy;
    let lisonsAreas = [];
    let lisonsAreasObj = [];
    let verticalLinesDraw = [];
    if (dicom_data?.lisonsAreasData.area_versions) {
      let area_versions = dicom_data?.lisonsAreasData?.area_versions;
      const lisonsColors = [
        "rgba(255, 0, 0, 0.09)",
        "rgba(0, 128, 0, 0.09)",
        "rgba(255, 255, 0, 0.09)",
        "rgba(0, 0, 255, 0.09)",
        "rgba(128, 0, 128, 0.09)",
      ];

      const pixelatedObject = area_versions?.find(
        (obj) => obj.area_type === objectType,
      );

      // if (dicom_data.isEtdrsActive) {
      //   addAreas(pixelatedObject);
      // }
      addAreas(pixelatedObject);
      if (displayType === "Edges") {
        lisonsAreasObj = [];
        cornerstoneTools.clearToolState(dcmRef.current, "MultiPointProbe");
        cornerstoneTools.clearToolState(dcmRef.current, "MultiLine");
        cornerstoneTools.addTool(MultiPointProbeTool);
        cornerstoneTools.setToolActive("MultiPointProbe", {
          mouseButtonMask: 1,
        });
        // cornerstoneTools.clearToolState(dcmRef.current, "MultiPointProbe");

        totalArea = roundNumbers(pixelatedObject.total_area_size_mm2);
        for (let i = 0; i < pixelatedObject.lesions.length; i++) {
          let concatenatedArray = [];
          lisonsAreasObj = [
            ...lisonsAreasObj,
            {
              label: `L${i + 1}`,
              label_coordinates: {
                x: midx,
                y: midy - 32,
              },
              area_coordinates: {
                x: 10,
                y:
                  opData.OP.metadata.height -
                  (pixelatedObject.lesions.length - i) * 64,
              },

              color: lisonsColors[i % lisonsColors.length],
              invalidated: true,
              area: roundNumbers(pixelatedObject.lesions[i].area_size_mm2),
            },
          ];
          // Add the tool to the enabledElementBinding
          lisonsAreas += `L${i + 1}:${roundNumbers(
            pixelatedObject.lesions[i].area_size_mm2,
          )}; `;

          concatenatedArray = pixelatedObject.lesions[i].vertical_lines.concat(
            pixelatedObject.lesions[i].horizontal_lines,
          );

          //
          const transformedArray = concatenatedArray.flatMap((line) =>
            line.map(([x, y]) => ({
              x,
              y,
              highlight: true,
              active: true,
              moving: false,
            })),
          );

          let x1 = pixelatedObject.lesions[i].horizontal_lines[0][0][0];
          let y1 = pixelatedObject.lesions[i].horizontal_lines[0][0][1];

          let x2 = pixelatedObject.lesions[i].horizontal_lines[0][1][0];
          let y2 = pixelatedObject.lesions[i].horizontal_lines[0][1][1];

          midx = Math.floor((x1 + x2) / 2);
          midy = Math.floor((y1 + y2) / 2);

          // Add the tool state to the enabledElementBinding
          cornerstoneTools.addToolState(
            dcmRef.current,
            "MultiPointProbe",
            {
              data: [
                {
                  visible: true,
                  active: true,
                  label: `L${i + 1}`,
                  label_coordinates: {
                    x: midx,
                    y: midy - 32,
                  },
                  area_coordinates: {
                    x: 10,
                    y:
                      opData.OP.metadata.height -
                      (pixelatedObject.lesions.length - i) * 64,
                  },
                  total_area: roundNumbers(pixelatedObject.total_area_size_mm2),
                  color: lisonsColors[i % lisonsColors.length],
                  invalidated: true,
                  area: roundNumbers(pixelatedObject.lesions[i].area_size_mm2),
                  handles: {
                    points: transformedArray,
                  },
                  // areas: {
                  //   lesions_1mm_area_mm2: pixelatedObject?.lesions_1mm_area_mm2,
                  //   lesions_1mm_percentage:
                  //     pixelatedObject?.lesions_1mm_percentage,
                  //   lesions_3mm_area_mm2: pixelatedObject?.lesions_3mm_area_mm2,
                  //   lesions_3mm_percentage:
                  //     pixelatedObject?.lesions_3mm_percentage,
                  //   lesions_6mm_area_mm2: pixelatedObject?.lesions_6mm_area_mm2,
                  //   lesions_6mm_percentage:
                  //     pixelatedObject?.lesions_6mm_percentage,
                  //   ETDRS_area_mm2: pixelatedObject?.ETDRS_area_mm2,
                  //   ETDRS_percentage: pixelatedObject?.ETDRS_percentage,
                  // },
                },
              ],
            },
            "enabledElementBinding",
          );
          cornerstone.updateImage(dcmRef.current);
          // cornerstoneTools.clearToolState(dcmRef.current, "MultiPointProbe");
        }
        cornerstoneTools.setToolPassive("MultiPointProbe");
      }

      if (displayType === "filled") {
        cornerstoneTools.clearToolState(dcmRef.current, "MultiPointProbe");
        cornerstoneTools.clearToolState(dcmRef.current, "MultiLine");
        cornerstoneTools.addTool(MultiLineTool);
        cornerstoneTools.setToolActive("MultiLine", {
          mouseButtonMask: 1,
        });
        // cornerstoneTools.clearToolState(dcmRef.current, "MultiLine");
        // area_versions = responseLison.area_versions;

        totalArea = roundNumbers(pixelatedObject.total_area_size_mm2);

        for (let i = 0; i < pixelatedObject.lesions.length; i++) {
          verticalLinesDraw = [
            ...verticalLinesDraw,
            pixelatedObject.lesions[i].horizontal_lines,
          ];
          let x1 = pixelatedObject.lesions[i].horizontal_lines[0][0][0];
          let y1 = pixelatedObject.lesions[i].horizontal_lines[0][0][1];

          let x2 = pixelatedObject.lesions[i].horizontal_lines[0][1][0];
          let y2 = pixelatedObject.lesions[i].horizontal_lines[0][1][1];
          lisonsAreas += `L${i + 1}:${roundNumbers(
            pixelatedObject.lesions[i].area_size_mm2,
          )}; `;

          lisonsAreasObj = [
            ...lisonsAreasObj,
            {
              label: `L${i + 1}`,
              label_coordinates: {
                x: Math.floor((x1 + x2) / 2),
                y: Math.floor((y1 + y2) / 2) - 32,
              },
              area_coordinates: {
                x: 10,
                y:
                  opData.OP.metadata.height -
                  (pixelatedObject.lesions.length - i) * 64,
              },

              color: lisonsColors[i % lisonsColors.length],
              invalidated: true,
              area: roundNumbers(pixelatedObject.lesions[i].area_size_mm2),
            },
          ];
        }
      }
      cornerstoneTools.addToolState(
        dcmRef.current,
        "MultiLine",
        {
          data: [
            {
              visible: true,
              active: true,
              labelsData: lisonsAreasObj,
              total_area: roundNumbers(pixelatedObject.total_area_size_mm2),
              invalidated: true,
              handles: {
                points: verticalLinesDraw,
              },
              // areas: {
              //   lesions_1mm_area_mm2: pixelatedObject?.lesions_1mm_area_mm2,
              //   lesions_1mm_percentage: pixelatedObject?.lesions_1mm_percentage,
              //   lesions_3mm_area_mm2: pixelatedObject?.lesions_3mm_area_mm2,
              //   lesions_3mm_percentage: pixelatedObject?.lesions_3mm_percentage,
              //   lesions_6mm_area_mm2: pixelatedObject?.lesions_6mm_area_mm2,
              //   lesions_6mm_percentage: pixelatedObject?.lesions_6mm_percentage,
              //   ETDRS_area_mm2: pixelatedObject?.ETDRS_area_mm2,
              //   ETDRS_percentage: pixelatedObject?.ETDRS_percentage,
              // },
            },
          ],
        },
        "enabledElementBinding",
      );
      cornerstone.updateImage(dcmRef.current);

      cornerstoneTools.setToolPassive("MultiLine");

      if (dicom_data?.takeScreenShot) {
        captureScreenshot();
      } else {
        dispatch({ type: "LoadingOverlay_changed", LoadingOverlay: false });
        dispatch({
          type: "Lison_Input_Submit_Status_changed",
          MlLisonsInputsSubmitStatus: false,
        });
        dispatch({
          type: "submitDisabled_changed",
          submitDisable: false,
        });
      }
    }

    if (dicom_data.selectedTool === "Lison") {
      cornerstoneTools.addTool(LisonTool);
      cornerstoneTools.setToolActive("Lison", { mouseButtonMask: 1 });
    }
    dispatch({
      type: "selected_area_Changed",
      SelectedArea: {
        TotalArea: totalArea,
        LisonsArea: lisonsAreas,
      },
    });
    getToolsState();
  };
  const prevLisonActiveRef = useRef(null);

  const setEtdrsEzdaValuesVisible = (visible) => {
    const etdrsState = cornerstoneTools.getToolState(dcmRef.current, "Etdrs");
    if (!etdrsState?.data) {
      return;
    }

    etdrsState.data.forEach((data) => {
      if (data?.areas?.ETDRS_area_mm2 !== undefined) {
        data.etdrsvaluesShow = visible;
      }
    });
  };

  useEffect(() => {
    if (!elmentEnabled) {
      return;
    }

    const wasActive = prevLisonActiveRef.current;
    const isActive = dicom_data.LisonActive;
    prevLisonActiveRef.current = isActive;

    if (wasActive === null) {
      if (hasSavedEzdaAnnotations()) {
        tryDrawLisonProbePoints();
      }
      if (isActive && dicom_data?.lisonsAreasData?.area_versions) {
        AddLisonAreas(
          dcmRef,
          dicom_data.lisonOptions.lisonAreaselected,
          dicom_data.lisonOptions.lisonDisplaySelected,
        );
      }
      return;
    }

    if (wasActive === isActive) {
      if (isActive && dicom_data?.lisonsAreasData?.area_versions) {
        AddLisonAreas(
          dcmRef,
          dicom_data.lisonOptions.lisonAreaselected,
          dicom_data.lisonOptions.lisonDisplaySelected,
        );
      }
      return;
    }

    if (!isActive) {
      cornerstoneTools.clearToolState(dcmRef.current, "MultiLine");
      cornerstoneTools.clearToolState(dcmRef.current, "MultiPointProbe");

      const lisonProbeState = cornerstoneTools.getToolState(
        dcmRef.current,
        "LisonProbe",
      );
      if (lisonProbeState?.data) {
        lisonProbeState.data.forEach((item) => {
          item.visible = false;
        });
      }

      setEtdrsEzdaValuesVisible(false);
    } else {
      tryDrawLisonProbePoints();

      if (dicom_data?.lisonsAreasData?.area_versions) {
        AddLisonAreas(
          dcmRef,
          dicom_data.lisonOptions.lisonAreaselected,
          dicom_data.lisonOptions.lisonDisplaySelected,
        );
      } else {
        setEtdrsEzdaValuesVisible(true);
      }
    }

    cornerstone.updateImage(dcmRef.current);
  }, [dicom_data.LisonActive, dicom_data.lisonOptions, elmentEnabled]);

  useEffect(() => {
    if (!elmentEnabled) {
      return;
    }

    const update_d = opData.OP.annotation_toggle;
    if (update_d === undefined) {
      return;
    }

    if (update_d.action_type === "Add_Lison_Length_point") {
      drawLisonProbePointsFromOpt();
      cornerstone.updateImage(dcmRef.current);
      return;
    }

    const toolState = cornerstoneTools.getToolState(
      dcmRef.current,
      update_d.annot_type,
    );

    if (toolState === null) {
      return;
    }

    if (update_d.action_type === "delete_LisonProbes") {
        const toolState = cornerstoneTools.getToolState(
          dcmRef.current,
          "LisonProbe",
        );

        if (toolState?.data) {
          toolState.data = [];
          // cornerstoneTools.clearToolState(dcmRef.current, "LisonProbe")
        }
        // toolState.data.splice(update_d.index, 1);
        getToolsState();
      }

      if (update_d.action_type === "delete") {
        const updatedData = {
          ...dicom_data?.lisonsAreasData,
          area_versions: dicom_data?.lisonsAreasData.area_versions.map(
            (area) => ({
              ...area,
              lesions: area.lesions.filter(
                (_, index) => index !== update_d.index,
              ),
            }),
          ),
        };
        dispatch({
          type: "Set_Lisons_Area_Data",
          lisonsAreasData: updatedData,
        });

        toolState.data.splice(update_d.index, 1);
        getToolsState();
      }
      cornerstone.updateImage(dcmRef.current);
  }, [opData.OP.annotation_toggle, elmentEnabled]);

  useEffect(() => {
    if (dicom_data?.takeScreenShot && dicom_data.SubmitLisonsStatus) {
      dispatch({
        type: "LoadingOverlay_changed",
        LoadingOverlay: false,
      });
      captureScreenshot();
      dispatch({
        type: "LoadingOverlay_changed",
        LoadingOverlay: false,
      });
    }
  }, [dicom_data.takeScreenShot]);

  useEffect(() => {
    if (!elmentEnabled) return;
  }, []);

  // Update ETDRS tool when API response data changes

  const add_Etdrs_circles = () => {
    let element = dcmRef.current;
    var rect = opData.OPT.metadata.rect;

    let area_versions = dicom_data?.lisonsAreasData?.area_versions;

    const pixelatedObject = area_versions?.find(
      (obj) => obj.area_type === dicom_data?.lisonOptions.lisonAreaselected,
    );

    var scale_x = opData.OP.metadata.pixel_spacing.columnPixelSpacing;
    var circles_radii = [1 / scale_x / 2, 3 / scale_x / 2, 6 / scale_x / 2];

    cornerstoneTools.clearToolState(element, "Etdrs");
    cornerstoneTools.addToolState(element, "Etdrs", {
      showEtdrsMirroring: dicom_data?.settings?.brush?.showEtdrsMirroring,
      visible: etdrsActive,
      etdrsvaluesShow: dicom_data?.LisonActive,
      rect: rect,
      circles_radii: circles_radii,
      currentFrame: dicom_data.currentFrame,
      frameLines: opData?.OPT?.metadata?.frames || [],
      optWidth:
        dicom_data?.dicom_data[query.get("stack_id")]?.OPT?.metadata?.meta
          ?.width,
      optViewerHeight:
        dicom_data?.dicom_data[query.get("stack_id")]?.OPT?.metadata?.meta
          ?.height,
      areas: {
        lesions_1mm_area_mm2: pixelatedObject?.lesions_1mm_area_mm2,
        lesions_1mm_percentage: pixelatedObject?.lesions_1mm_percentage,
        lesions_3mm_area_mm2: pixelatedObject?.lesions_3mm_area_mm2,
        lesions_3mm_percentage: pixelatedObject?.lesions_3mm_percentage,
        lesions_6mm_area_mm2: pixelatedObject?.lesions_6mm_area_mm2,
        lesions_6mm_percentage: pixelatedObject?.lesions_6mm_percentage,
        ETDRS_area_mm2: pixelatedObject?.ETDRS_area_mm2,
        ETDRS_percentage: pixelatedObject?.ETDRS_percentage,
      },
      circleData: etdrsValuesFromSegmentationApi,
      volumesData: etdrsVolumesFromSegmentationApi,
      handles: {
        start: {
          x: (rect[2] - rect[0]) / 2 + rect[0],
          y: (rect[3] - rect[1]) / 2 + rect[1],
        },
        end: {
          x: rect[2],
          y: rect[3],
        },
      },
    });

    // Store dicomRefBscan separately using a WeakMap
    toolStateMap.set(element, {
      dicomRefBscan: dicom_data?.dicomRefBscan,
    });

    cornerstone.updateImage(dicom_data?.dicomRefBscan.current);
    getToolsState();
  };
  const add_thickness_map_grid = (image) => {
    let element = dcmRef.current;
    var rect = opData.OPT.metadata.rect;

    var scale_x = opData.OP.metadata.pixel_spacing.columnPixelSpacing;
    var circles_radii = [1 / scale_x / 2, 3 / scale_x / 2, 6 / scale_x / 2];

    cornerstoneTools.clearToolState(element, "ThicknessMap");
    cornerstoneTools.addToolState(element, "ThicknessMap", {
      rect: rect,
      circles_radii: circles_radii,
      thickness_image: image,
      handles: {
        start: {
          x: (rect[2] - rect[0]) / 2 + rect[0],
          y: (rect[3] - rect[1]) / 2 + rect[1],
        },
        end: {
          x: rect[2],
          y: rect[3],
        },
      },
    });

    cornerstoneTools.setToolPassive("ThicknessMap", { mouseButtonMask: 1 });
  };

  const metaDataProvider = (type, imageId) => {
    // Custom meta data provider for the viewport(OCT)
    if (
      type === "imagePlaneModule" &&
      dicom_data.selectedTool === "ThicknessMap"
    ) {
      if (imageId.includes(opData.OP.metadata.url)) {
        return {
          rowPixelSpacing: opData.OP.metadata.pixel_spacing.rowPixelSpacing,
          columnPixelSpacing:
            opData.OP.metadata.pixel_spacing.columnPixelSpacing,
        };
      }
    } else if (type === "imagePlaneModule") {
      if (imageId.includes(opData.OP.metadata.url)) {
        return {
          rowPixelSpacing: opData.OP.metadata.pixel_spacing.rowPixelSpacing,
          columnPixelSpacing:
            opData.OP.metadata.pixel_spacing.columnPixelSpacing,
        };
      }
    }
  };

  useEffect(() => {
    cornerstone.metaData.addProvider(metaDataProvider);
  }, [Object.keys(dicom_data.dicom_data).length, dicom_data.selectedTool]);

  useEffect(() => {
    if (!elmentEnabled) {
      return;
    }
    if (
      dicom_data.selectedTool === "ThicknessMap" ||
      dicom_data.selectedTool === "Etdrs"
    ) {
      // On U key press, we calculate thickness map for 2 selected layers and overlay on Fundus image

      var layers = cornerstone.getLayers(dcmRef.current);
      if (layers.length > 1) {
        cornerstone.removeLayer(dcmRef.current, layers[1].layerId);
      }
      cornerstone.updateImage(dcmRef.current);
      if (dicom_data.selectedTool === "Etdrs") {
        const toolData = cornerstoneTools.getToolState(dcmRef.current, "Etdrs");
        if (toolData === undefined && dicom_data.isEtdrsActive) {
          add_Etdrs_circles();
        }
        return;
      }
      var first_point = opData.OPT.metadata.frames[0];
      var last_point =
        opData.OPT.metadata.frames[opData.OPT.metadata.frames.length - 1];

      // Calculate width and height of rectangle
      var width = Math.abs(first_point[0] - last_point[2]);
      var height = Math.abs(first_point[1] - last_point[1]);

      var imgs = get_thickness_map(dcmRef.current, opData.OPT.stack.imageIds, {
        width: opData.OPT.metadata.meta.width,
        height: opData.OPT.metadata.meta.width,
        rowPixelSpacing: opData.OPT.metadata.pixel_spacing.rowPixelSpacing,
        columnPixelSpacing:
          opData.OPT.metadata.pixel_spacing.columnPixelSpacing,
        lut: dicom_data.thicknessLUT,
        angle: opData.OPT.metadata.rect_angle,
        rect: opData.OPT.metadata.rect,
        rect_wh: [width, height],
        op_meta: opData.OP.metadata,
        frames: [
          opData.OPT.metadata.frames[0],
          opData.OPT.metadata.frames[opData.OPT.metadata.frames.length - 1],
        ],
        total_frames: opData.OPT.metadata.frames.length,
      });

      if (imgs === null) {
        return;
      }
      var img = imgs.cnsImage;
      img.rowPixelSpacing = opData.OP.metadata.pixel_spacing.rowPixelSpacing;
      img.columnPixelSpacing =
        opData.OP.metadata.pixel_spacing.columnPixelSpacing;

      add_thickness_map_grid(imgs.thickness_image_array);

      cornerstone.addLayer(dcmRef.current, img, {
        name: "Thickness",
        opacity: 0.7,
        visible: true,
      });
      cornerstone.updateImage(dcmRef.current);
      cornerstone.updateImage(dicom_data?.dicomRefBscan.current);
    } else {
      // Remove thickness map layer if any
      var layers = cornerstone.getLayers(dcmRef.current);
      if (layers.length > 1) {
        cornerstoneTools.clearToolState(dcmRef.current, "ThicknessMap");
        cornerstone.removeLayer(dcmRef.current, layers[1].layerId);
        cornerstone.updateImage(dcmRef.current);
        cornerstone.updateImage(dicom_data?.dicomRefBscan.current);
      }
    }
  }, [dicom_data.selectedTool]);

  const layers = [
    {
      imageId: opData.OP.stack.imageIds[0],
      options: {
        name: "OP",
        opacity: 0.7,
      },
    },
  ];

  const loadImages = () => {
    const promises = [];

    layers.forEach(function (layer) {
      const loadPromise = cornerstone.loadAndCacheImage(layer.imageId);
      promises.push(loadPromise);
    });

    return Promise.all(promises);
  };

  const applySafeViewport = (element, image) => {
    if (!image) {
      return;
    }

    const viewport = cornerstone.getDefaultViewportForImage(element, image);
    const safeVoi = resolveSafeViewportVoi(image);
    if (safeVoi) {
      viewport.voi = safeVoi;
    }

    if (image.photometricInterpretation === "MONOCHROME1") {
      viewport.invert = true;
    }

    cornerstone.setViewport(element, viewport);
  };

  const clearAnnotationToolState = () => {
    if (!isCornerstoneElementEnabled(dcmRef.current)) {
      return;
    }

    [...dicom_data.drawingTools, "Etdrs"].forEach((tool) => {
      safeClearToolState(dcmRef.current, tool);
    });
  };

  const addFilteredAnnotations = () => {
    const keysToRemove = ["LisonProbe"];
    let filteredObj = { ...(props.annotations || {}) };

    clearAnnotationToolState();

    if (Object.keys(filteredObj).length > 0) {
      if (!dicom_data.LisonActive) {
        filteredObj = Object.keys(props.annotations || {})
          .filter((key) => !keysToRemove.includes(key))
          .reduce((acc, key) => {
            acc[key] = props.annotations[key];
            return acc;
          }, {});
      }
      if (filteredObj.lisonsAreasData) delete filteredObj.lisonsAreasData;
      addAnnotations(
        filteredObj,
        dcmRef,
        etdrsActive,
        dicom_data,
        query.get("stack_id"),
      );
      // Object.entries(filteredObj).forEach(([annotation_type, annotations]) => {
      //   if (annotation_type === "Etdrs") {

      //     // Add this code to click the ETDRS button
      //     const etdrsButton = document.getElementById("etdrs-button");
      //     if (etdrsButton) {
      //       etdrsButton.click();
      //     }

      //     setTimeout(() => {
      //       const etdrsCircles = document.getElementById("etdrs-circles");
      //       if (etdrsCircles) {
      //         etdrsCircles.click();
      //       }
      //     }, 3000); // 1000ms = 1 second
      //   }
      // }      );
    }
    safeUpdateImage(dcmRef.current);
    syncEtdrsDisplayOnOp(dcmRef.current, dicom_data.currentFrame, etdrsActive);
    getToolsState();
  };
  useEffect(() => {
    if (!elmentEnabled) {
      return;
    }
    addFilteredAnnotations();
    if (hasSavedEzdaAnnotations()) {
      tryDrawLisonProbePoints();
    }
    if (props.annotationSourceVersion > 0) {
      window.requestAnimationFrame(() => {
        props.onAnnotationSourceRendered?.();
      });
    }
  }, [props.annotationSourceVersion, elmentEnabled]);
  useEffect(() => {
    let element = dcmRef.current;
    //cornerstone.loadImage(opData.OP.stack.imageIds[0]).then(function (image) {
    //  cornerstone.enable(element);
    //    cornerstone.displayImage(element, image)
    loadImages()
      .then(function (images) {
        cornerstone.enable(element);
        setElementEnabled(true);

        images.forEach(function (image, index) {
          const layer = layers[index];
          cornerstone.addLayer(element, image, layer.options);

          cornerstone.updateImage(element);
        });
        applySafeViewport(element, images[0]);
      })
      .then(function () {
        if (!isCornerstoneElementEnabled(element)) {
          return;
        }

        //cornerstoneTools.addStackStateManager(element, ['stack']);
        //cornerstoneTools.addToolState(element, 'stack', opData.OP.stack);

        addRectangleStatic();
        addLineStatic();
        addThicknessMapStatic();

        element.addEventListener("cornerstoneelementresized", onWindowResize);

        drawRectangleStatic(
          dcmRef,
          opData.OPT.metadata.rect,
          -opData.OPT.metadata.rect_angle,
        );
        try {
          cornerstone.drawImage(dcmRef.current);
        } catch (_error) {
          /* noop */
        }

        // Get viewport
        addFilteredAnnotations();

        // add_thickness_map_grid(null)
      });
  }, [Object.keys(dicom_data.dicom_data).length]);

  const onMouseDown = (e) => {
    // Record initial position of static rectangle is shift is pressed
    // cornerstoneTools.clearToolState(dcmRef.current, "Lison");
    cornerstoneTools.setToolPassive("Lison");
  };

  const capturePreMoveEtdrsCenter = () => {
    if (!etdrsActive || !dcmRef.current) return;
    const etdrsState = cornerstoneTools.getToolState(dcmRef.current, "Etdrs");
    const start = etdrsState?.data?.[0]?.handles?.start;
    if (start?.x != null && start?.y != null) {
      preMoveEtdrsCenterRef.current = { x: start.x, y: start.y };
    }
  };

  function onMouseOver(evt) {
    // Your event handling code here
  }

  const onMouseUp = async (e) => {
    if (!etdrsActive) {
      return;
    }

    // Simulate getting the `etdrsState` data from your cornerstoneTools
    const etdrsState = cornerstoneTools.getToolState(dcmRef.current, "Etdrs");
    const currentStart = etdrsState.data[0].handles.start;

    // debugger;
    // Compare the current start object with the previous one
    var annotations = get_annotations(
      dicom_data.dicom_data,
      dicom_data.drawingTools,
    );

    if (
      (prevStartRef?.x !== currentStart?.x ||
        prevStartRef?.y !== currentStart?.y ||
        !prevStartRef) &&
      etdrsState?.data[0]?.visible
    ) {
      let lisonLines = extractPointsCoordinates(
        annotations[query.get("stack_id")].OPT,
      );

      if (ezdalinescheck(annotations[query.get("stack_id")].OPT) > 0) {
        const clickProcess = () => {
          const button = document.getElementById("EzdaProcess");
          if (button) {
            button.click();
          }
        };

        // Ensure lesion toggle is ON before processing
        if (!dicom_data?.LisonActive) {
          const lesionToggle = document.getElementById("lesion-toggle");
          if (lesionToggle && !lesionToggle.disabled && !lesionToggle.checked) {
            lesionToggle.click();
            setTimeout(clickProcess, 300);
          } else {
            clickProcess();
          }
        } else {
          clickProcess();
        }
      } else {
        setPendingEtdrsCenter({
          x: currentStart.x,
          y: currentStart.y,
        });
        setEtdrsMoveModalOpen(true);
      }
      // prevStartRef.current = { ...currentStart };

      //
    }

    // Update the ref with the new start object
  };

  useEffect(() => {
    const element = dcmRef.current;
    if (!element) return;

    element.addEventListener("cornerstonetoolsmousedown", onMouseDown);
    element.addEventListener("cornerstonetoolsmousewheel", onMouseWheel);
    element.addEventListener("cornerstonetoolsmousedrag", onMouseDrag);
    element.addEventListener("cornerstonetoolsmouseclick", onMouseClick);
    element.addEventListener("cornerstonetoolsmouseup", onMouseUp);
    element.addEventListener(
      "cornerstonetoolsmeasurementadded",
      onMeasurementAdded,
    );
    element.addEventListener(
      "cornerstonetoolsmeasurementmodified",
      onMeasurementModified,
    );
    // Capture phase runs before ETDRS tool moves the grid on mousedown.
    element.addEventListener("mousedown", capturePreMoveEtdrsCenter, true);

    return () => {
      element.removeEventListener("cornerstonetoolsmousedown", onMouseDown);
      element.removeEventListener("cornerstonetoolsmousewheel", onMouseWheel);
      element.removeEventListener("cornerstonetoolsmousedrag", onMouseDrag);
      element.removeEventListener("cornerstonetoolsmouseclick", onMouseClick);
      element.removeEventListener("cornerstonetoolsmouseup", onMouseUp);
      element.removeEventListener(
        "cornerstonetoolsmeasurementadded",
        onMeasurementAdded,
      );
      element.removeEventListener(
        "cornerstonetoolsmeasurementmodified",
        onMeasurementModified,
      );
      element.removeEventListener("mousedown", capturePreMoveEtdrsCenter, true);
    };
  }, [
    dicom_data.currentFrame,
    dicom_data.selectedTool,
    dicom_data.selectedKey,
    dicom_data.processing,
    etdrsActive,
    prevStartRef,
  ]);

  useEffect(() => {
    if (!elmentEnabled || !dcmRef.current) return;

    syncEtdrsDisplayOnOp(dcmRef.current, dicom_data.currentFrame, etdrsActive);

    const bscanEl = dicom_data?.dicomRefBscan?.current;
    if (bscanEl && isCornerstoneElementEnabled(bscanEl)) {
      try {
        cornerstone.updateImage(bscanEl);
      } catch (_error) {
        /* noop */
      }
    }
  }, [
    dicom_data.currentFrame,
    elmentEnabled,
    etdrsActive,
    props.annotations,
    props.annotationSourceVersion,
  ]);

  const handleEtdrsMoveConfirm = async () => {
    setEtdrsMoveModalOpen(false);
    if (!pendingEtdrsCenter) return;

    const stackId = query.get("stack_id");
    const imageIds = dicom_data.dicom_data?.[stackId]?.OPT?.stack?.imageIds;

    try {
      const volumes = await getEtdrsVolumesFromSegmentationApi(
        pendingEtdrsCenter,
        buildVolumesSaveParams(query),
      );
      const etdrsState = cornerstoneTools.getToolState(dcmRef.current, "Etdrs");
      if (volumes && etdrsState?.data) {
        etdrsState.data.forEach((data) => {
          data.volumesData = volumes;
        });
      }
      dispatch({
        type: "SET_PREV_START_REF",
        payload: { ...pendingEtdrsCenter },
      });

      if (imageIds?.length) {
        snapshotFluidBaselinesForStack(
          stackId,
          imageIds,
          dicom_data?.activeFluidLabels,
        );
      }
      dispatch({ type: "CLEAR_ALL_FLUID_DIRTY_FRAMES" });

      getToolsState();
      cornerstone.updateImage(dcmRef.current);
      const bscanEl = dicom_data?.dicomRefBscan?.current;
      if (bscanEl) {
        cornerstone.updateImage(bscanEl);
      }
    } catch (error) {
      console.error("Error applying ETDRS move:", error);
      alert("Failed to update ETDRS volumes. Please try again.");
    } finally {
      setPendingEtdrsCenter(null);
    }
  };

  const getLastConfirmedEtdrsCenter = () => {
    const confirmed = prevStartRefLatest.current;
    if (confirmed?.x != null && confirmed?.y != null) {
      return { x: confirmed.x, y: confirmed.y };
    }
    const preMove = preMoveEtdrsCenterRef.current;
    if (preMove?.x != null && preMove?.y != null) {
      return { x: preMove.x, y: preMove.y };
    }
    return null;
  };

  const restoreEtdrsCenter = (center) => {
    if (!center || !dcmRef.current) return;
    const etdrsState = cornerstoneTools.getToolState(dcmRef.current, "Etdrs");
    if (!etdrsState?.data?.length) return;

    etdrsState.data.forEach((data) => {
      if (!data?.handles) return;
      data.handles.start = {
        ...data.handles.start,
        x: center.x,
        y: center.y,
      };
      data.handles.end = {
        ...data.handles.end,
        x: center.x,
        y: center.y,
      };
    });
    getToolsState();
    cornerstone.updateImage(dcmRef.current);
  };

  const handleEtdrsMoveCancel = () => {
    restoreEtdrsCenter(getLastConfirmedEtdrsCenter());
    setEtdrsMoveModalOpen(false);
    setPendingEtdrsCenter(null);
  };

  return (
    <>
      <EtdrsMoveConfirmModal
        open={etdrsMoveModalOpen}
        onConfirm={handleEtdrsMoveConfirm}
        onCancel={handleEtdrsMoveCancel}
      />
      <div
        style={{
          height: "100%",
          width: props.width,
          backgroundColor: "black",
          marginBottom: "45px",
        }}
        ref={dcmRef}
      ></div>
    </>
  );
}
